import * as React from "react"
import * as styles from "./footer.css"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { default as footerQuery } from "../staticQueries/footer"
import footerColumnOne from "../staticQueries/footerColumnOne"
import footerColumnTwo from "../staticQueries/footerColumnTwo"
import footerColumnThree from "../staticQueries/footerColumnThree"
import footerColumnFour from "../staticQueries/footerColumnFour"
import sponsors from "../staticQueries/sponsors"
import { TNavigationJson } from "../types"
import BumbershootLogoSquareSVG from "../graphics/Bumbershoot-Logo-square-white.svg"
import TikTok from "../graphics/icon-tiktok.svg"
import Instagram from "../graphics/icon-instagram.svg"
import Facebook from "../graphics/icon-facebook.svg"
import Twitter from "../graphics/icon-twitter.svg"
import Youtube from "../graphics/icon-youtube.svg"

import {
  FlexList,
  NavLink,
  Link,  
  VisuallyHidden,
} from "./ui"


const socialIcons = {
  TikTok: TikTok,
  Instagram: Instagram, 
  Twitter: Twitter,
  Facebook: Facebook,
  Youtube: Youtube,
};

export default function Footer() {
  const footer = footerQuery()

  let footerColumnOneJson, footerColumnTwoJson, footerColumnThreeJson, footerColumnFourJson;
 
  try {
    footerColumnOneJson = JSON.parse(footerColumnOne().json)
  } catch (error) {}
  try {
    footerColumnTwoJson = JSON.parse(footerColumnTwo().json)
  } catch (error) {}
  try {
    footerColumnThreeJson = JSON.parse(footerColumnThree().json)
  } catch (error) {}
  try {
    footerColumnFourJson = JSON.parse(footerColumnFour().json)
  } catch (error) {}

  const navigationColOne: TNavigationJson[] = footerColumnOneJson
  const navigationColTwo: TNavigationJson[] = footerColumnTwoJson
  const navigationColThree: TNavigationJson[] = footerColumnThreeJson
  const navigationColFour: TNavigationJson[] = footerColumnFourJson

  function filterSponsorsByCategory(sponsors: any[], categorySlug: string): any[] {
    return sponsors.filter(sponsor => 
        sponsor.sponsorCategories.some(category => 
            category.slug === categorySlug
        )
    );
}

const filteredPremierePartners = filterSponsorsByCategory(sponsors(), 'premier-sponsors');
const filteredSponsors = filterSponsorsByCategory(sponsors(), 'sponsor');
const filteredCommunitySupportersTop = filterSponsorsByCategory(sponsors(), 'community-supporter-tier-1');
const filteredCommunitySupportersBottom = filterSponsorsByCategory(sponsors(), 'community-supporter-tier-2');
const filteredHotelSupportersBottom = filterSponsorsByCategory(sponsors(), 'hotel-partners');
  

  function getSponsorImageStyle(size: string = "normal") {
    switch (size) {
      case "larger":
        return { width: '240px', height: 'auto' };
      case "medium":
        return { width: '180px', height: 'auto' };
      case "normal":
        return { width: '100px', height: 'auto' };
      case "smaller":
          return { width: '75px', height: 'auto' };    
      default:
        return { width: '100px', height: 'auto' };
    }
  }

  const SponsorList = ({ title, sponsors, styles, size }) => {
    if (sponsors.length === 0) return null;
    
    return (
      <div style={{textAlign:"center"}}>
        <h2 className={styles.sponsorsTitle}>{title}</h2>
        <div className={styles.sponsors}>
          { sponsors.map((sponsor) => {
            const sponsorImage = getImage(sponsor.logo); 
            const imageStyle = getSponsorImageStyle(size); // Determine the style based on the sponsor's attributes
          
            return (
              sponsorImage ? (
                <a key={sponsor.id} href={sponsor.link} target="_blank" rel="noopener noreferrer" className={styles.sponsorLogo}>
                    <GatsbyImage
                      alt={sponsor.title}
                      image={sponsorImage}
                      className={styles.imageStyle}
                      style={imageStyle}
                      objectFit="contain"
                    />
                </a>
              ) : null
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.sponsorsContainer}>
      <SponsorList title="Sponsors" sponsors={filteredPremierePartners} styles={styles} size="larger" />
      <SponsorList title="" sponsors={filteredSponsors} styles={styles} size="normal"/>
      <br/><br/>
      <SponsorList title="Community Supporters" sponsors={filteredCommunitySupportersTop} styles={styles} size="smaller"/>
      <SponsorList title="" sponsors={filteredCommunitySupportersBottom} styles={styles} size="smaller" />
      <br/><br/>
      <SponsorList title="Hotel Partners" sponsors={filteredHotelSupportersBottom} styles={styles} size="medium" />
     
      <footer className={styles.footerContainer}>
        <div className={styles.footerContent}>
          <div className={styles.frames}>
            <div className={styles.footerLeft}>
              <Link className={styles.footerBSLogo} to={"/"}>
                <VisuallyHidden>Bumbershoot Logo</VisuallyHidden>
                <BumbershootLogoSquareSVG />
              </Link>
              <div>
                <h3 className={styles.footerTitle}>{footer.title}</h3>
                <iframe className={styles.iframe} scrolling="no" src="/hive-email.html" />
              </div>
            </div>
            <div className={styles.social}>
              {footer.socialNetworks.map((social) => {
                const Icon = socialIcons[social.network]
                return (
                  <a className={styles.networkIconContainer} key={social.network} href={social.link} target="_blank">
                    <VisuallyHidden>{social.network}</VisuallyHidden>
                    <Icon className={styles.networkIcon} />
                    
                  </a>
                )
              })}
            </div>
          </div>
          <div className={styles.terms}>
            <FlexList alignItems="start" variant="column" className={styles.footerNavList}>
              {navigationColOne?.map((navItem, i: number) => (
                <li key={i}>
                  <NavLink to={navItem.link} 
                    target={navItem.label === "Buy Tickets" || navItem.label === "Accommodations" ? "_blank" : null}
                    className={{}}
                  >
                    {navItem.label}
                    {navItem.blurb && (
                      <span style={{ fontSize: "0.9em", fontWeight: "400", display: "block" }}>{navItem.blurb}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </FlexList>
            <FlexList alignItems="start" variant="column" className={styles.footerNavList}>
              {navigationColTwo?.map((navItem, i: number) => (
                <li key={i}>
                  <NavLink to={navItem.link} className={{}}>
                    {navItem.label}
                    {navItem.blurb && (
                      <span style={{ fontSize: "0.9em", fontWeight: "400", display: "block" }}>{navItem.blurb}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </FlexList>
            <FlexList alignItems="start" variant="column" className={styles.footerNavList}>
              {navigationColThree?.map((navItem, i: number) => (
                <li key={i}>
                  <NavLink to={navItem.link} className={{}}>
                    {navItem.label}
                    {navItem.blurb && (
                      <span style={{ fontSize: "0.9em", fontWeight: "400", display: "block" }}>{navItem.blurb}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </FlexList>
            <FlexList alignItems="start" variant="column" className={styles.footerNavList}>
              {navigationColFour?.map((navItem, i: number) => (
                <li key={i}>
                  <NavLink to={navItem.link} className={{}}>
                    {navItem.label}
                    {navItem.blurb && (
                      <span style={{ fontSize: "0.9em", fontWeight: "400", display: "block" }}>{navItem.blurb}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </FlexList>        
          </div>
        </div>
      </footer>
    </div>
  )
}
